import React, { useEffect, useState } from 'react';
import { ChevronDown, Target, Users, Briefcase, Cog, Network, Eye, Brain, Cpu, Shield, Globe, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    industry: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mock form submission for now
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting Virtual Red Air. We'll get back to you soon.",
    });
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      industry: '',
      message: ''
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-red-900/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold">
              <span className="text-white">Virtual</span>
              <span className="text-red-600"> Red Air</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('welcome')} className="hover:text-red-500 transition-colors">Welcome</button>
              <button onClick={() => scrollToSection('about')} className="hover:text-red-500 transition-colors">About Us</button>
              <button onClick={() => scrollToSection('vision')} className="hover:text-red-500 transition-colors">Mission</button>
              <button onClick={() => scrollToSection('services')} className="hover:text-red-500 transition-colors">Services</button>
              <button onClick={() => scrollToSection('technologies')} className="hover:text-red-500 transition-colors">Technologies</button>
              <button onClick={() => scrollToSection('why-vra')} className="hover:text-red-500 transition-colors">Why VRA</button>
              <button onClick={() => scrollToSection('contact')} className="hover:text-red-500 transition-colors">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_vra-insights/artifacts/9rl30tle_photo%201.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">Fighter Pilot Mindset</span>
            <br />
            <span className="text-red-500">Forging the Future</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            through distributed synthetic training ecosystems for modern defense, security and commercial environments
          </p>
          <Button 
            onClick={() => scrollToSection('welcome')} 
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-md transition-all transform hover:scale-105"
          >
            Discover More
            <ChevronDown className="ml-2 h-5 w-5" />
          </Button>
        </div>
        
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-8 w-8 text-red-500" />
        </div>
      </section>

      {/* Welcome Section */}
      <section id="welcome" className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              <span className="text-white">Welcome to Virtual</span> <span className="text-red-500">Red Air</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Image */}
            <div className="order-2 md:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_vra-insights/artifacts/5twdiibx_photo%202.png" 
                alt="Virtual Red Air Training"
                className="rounded-lg shadow-2xl w-full h-auto border border-red-900/30"
              />
            </div>
            
            {/* Text Content */}
            <div className="order-1 md:order-2">
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Welcome to Virtual Red Air (VRA) - a company primarily dedicated to advancing fighter pilot readiness through distributed synthetic training ecosystems built for modern defence and security environments.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Operating at the convergence of <span className="text-red-400 font-semibold">Tactics, Technology, Trust and Transformation</span>, VRA supports armed forces, public authorities, and industry partners in developing and exploiting realistic, interoperable, and future-ready synthetic training capabilities.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                In an operational landscape defined by complexity, multidomain threats, and coalition operations, Virtual Red Air enables organizations to train as they will fight through connected ecosystems that bring together people, platforms, and technologies across national and organizational boundaries.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Moreover, the fighter pilot mentality may empower business leaders and teams to sharpen decision-making, lead with confidence under pressure, and significantly improve organizational performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">About</span> <span className="text-red-500">Us</span>
            </h2>
            
            {/* Our Vision Box */}
            <div className="max-w-4xl mx-auto mt-12 mb-12">
              <Card className="bg-gradient-to-br from-red-950/30 to-black/50 border-red-900/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-3xl text-center">
                    <span className="text-white">Our</span> <span className="text-red-500">Vision</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-gray-300 text-center leading-relaxed">
                    Our vision is to be a trusted enabler of multinational Synthetic Training ecosystems within NATO, the European Union, and allied nations, revolutionizing the way training and exercises are conceived and delivered, always keeping Greek priorities at the forefront.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <Card className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-600/20 rounded-lg">
                    <Users className="h-8 w-8 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl text-white">Who We Are</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base leading-relaxed">
                  Virtual Red Air is a team of highly experienced professionals with extensive backgrounds in military operations, training, simulation, and emerging digital environments, including significant hands-on experience within NATO structures and multinational programmes.
                  <br /><br />
                  Above all, we are a force committed to advancing the Synthetic World.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-600/20 rounded-lg">
                    <Target className="h-8 w-8 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl text-white">What We Do</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base leading-relaxed">
                  Virtual Red Air delivers end-to-end support across the full spectrum of Synthetic Training development and employment.
                  <br /><br />
                  Our core activities include specialized consulting and training services, robust project management, targeted research and development, and acting as a Synthetic Training Broker to connect stakeholders across nations, domains, and industries.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-600/20 rounded-lg">
                    <Shield className="h-8 w-8 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl text-white">Why We Do It</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base leading-relaxed">
                  Modern military operations demand seamless coordination across land, air, sea, space, and cyberspace. Traditional live exercises, while essential, are costly, constrained, and unable to fully prepare forces for emerging threats.
                  <br /><br />
                  Virtual Red Air exists to shape the next era of combat readiness and business leadership, preserving decades of operational expertise and applying it through the growing Synthetic World.
                  <br /><br />
                  Retired fighter pilots can keep flying and contributing long after hanging up the flight suit. No more G-forces pounding their bodies or cockpit wear and tear. Just mission focus - from more suitable places, serving a wider allied community, in parallel promoting the most rewarding career.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-600/20 rounded-lg">
                    <Network className="h-8 w-8 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl text-white">How We Do It</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base leading-relaxed">
                  Advances in disruptive technologies now make it possible to build rich, scalable synthetic environments where teams can develop, test, and refine skills against the threats of tomorrow.
                  <br /><br />
                  VRA acts as a catalyst within the Synthetic Training domain, actively promoting collaboration among armed forces, public authorities, industry, commercial partners, academia, NATO and EU entities.
                  <br /><br />
                  By fostering trust-based partnerships and shared understanding, we contribute to the development of interconnected training ecosystems that enhance interoperability, performance, and collective readiness, including through wargaming.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section id="vision" className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-red-900 to-transparent"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-white">Our Mission &</span> <span className="text-red-500">Values</span>
              </h2>
            </div>
            
            <Card className="bg-gradient-to-br from-red-950/30 to-black/50 border-red-900/50 backdrop-blur-sm mb-12">
              <CardHeader>
                <CardTitle className="text-3xl text-center text-white">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-300 text-center leading-relaxed">
                  Virtual Red Air prepares organizations for modern and future operational challenges through advanced synthetic training ecosystems enabled by the Fighter Pilot Mindset.
                </p>
              </CardContent>
            </Card>

            <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
              {[
                { icon: Target, title: 'Operational Relevance', desc: 'We ground every activity in real-world operational experience, ensuring that Synthetic Training solutions reflect actual mission demands and threat environments.' },
                { icon: Users, title: 'Collaboration & Trust', desc: 'We actively foster collaboration among nations, services, industry, academia, and NATO and EU entities to create cohesive and trusted training ecosystems.' },
                { icon: Globe, title: 'Interoperability by Design', desc: 'Interoperability is a foundational principle guiding our concepts, architectures, and partnerships, enabling multinational forces to train together seamlessly.' },
                { icon: Brain, title: 'Innovation with Purpose', desc: 'We leverage emerging technologies and advanced methodologies to solve real operational challenges with practical, scalable solutions.' },
                { icon: Award, title: 'Knowledge Preservation', desc: 'We are committed to preserving critical expertise gained through decades of service and ensuring its effective transfer to future generations.' },
              ].map((value, index) => (
                <Card key={index} className="bg-black/50 border-red-900/30 backdrop-blur-sm hover:border-red-600/50 transition-all hover:transform hover:scale-105 w-full md:w-[calc(33.333%-1rem)] lg:w-[calc(33.333%-1rem)]">
                  <CardHeader>
                    <div className="flex flex-col items-center text-center gap-3">
                      <div className="p-3 bg-red-600/20 rounded-full">
                        <value.icon className="h-6 w-6 text-red-500" />
                      </div>
                      <CardTitle className="text-lg text-white">{value.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-400 text-center">{value.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Continue in next file... */}
    </div>
  );
};

export default Home;